import { useCallback, useEffect, useMemo } from "react"
import { useApolloClient } from "@apollo/client"

import { useAppSelector, useAppDispatch, RootState } from '../../../Store'
import { gqlActions } from "../gqlData_Reducer"

/* eslint-disable */
import { USER_LOGIN, LoginResponse, LoginArgs, User_Data, USER_INFO, UserInfoResponse, UPDATE_USER_INFO, UpdateUserInfoArgs, UpdateUserInfoResponse, Def_MutationResponse, CreateUserResponse, CREATE_USER, CreateArgs} from "./GQLCalls/userCalls"
import { UserInfoArgs } from "./GQLCalls/userCalls"

import { UserStates, UserActions } from "./UserSlices/userSlice"
import { PersistPartial } from "redux-persist/lib/persistReducer";
import { CREATE_RESET_PASSWORD_ENTRY, CreateResetResponse, TOKEN_RESET_PASSWORD, TokenResetPassword, TokenResetResponse } from "./GQLCalls/passwordCalls"
import { CompanySettings, CompanySettingsResponse, GET_COMPANY_SETTINGS, UPDATE_COMPANY_SETTINGS, UpdateCompanySettingsResponse } from "./GQLCalls/companySettingsCalls"
import { Farm_Data, FARM_INFO, FarmInfoArgs, FarmInfoResponse, UserFarm_Data, FarmsResponse, UPDATE_FARM_INFO, UpdateFarmInfoArgs, UpdateFarmInfoResponse, USER_FARMS, UserFarmsArgs } from "./GQLCalls/farmCalls"
import { GET_SERVER_IP } from "./GQLCalls/getServerIp"
// import { setStates } from "../../../Store/StateHandlers/active-slice";
import { useDispatch } from "react-redux"
import { DateTime } from "luxon"
import { ADD_DOMAIN_WORKER, AddDomainWorkerResponse, REMOVE_DOMAIN_USER, RemoveDomainUserResponse, SET_USER_ATTENTION_EMAIL_SYSTEMS, SetUserAttentionEmailSystemsResponse, UsersWithGroups, WORKER_LIST, WorkerListResponse } from "./GQLCalls/workerListCalls"
import { LocaleFarmDate } from "../../../Definitions/localeFarmDate"
import { ADMIN_INFO, AdminInfo, AdminInfoResponse } from "./GQLCalls/adminInfoCalls"
import { SYSTEM_EMAILS_INFO, SYSTEM_EMAILS_MUTATION, SystemEmailsInfoResponse, SystemEmailsItem, SystemEmailsMutationResponse } from "./GQLCalls/userEmailCalls"
/* eslint-enable */

const defaultTimezone = "Europe/Copenhagen" as const;

export type UseUser = {
  doLogin:(username:string, password:string) => Promise<boolean>,
  doLogOut:() => void,
  createNewUser:(username: string, password: string, firstname:string, lastname:string, email:string, phone:string)=>Promise<boolean>,
  createUserResetToken: (username: string) => Promise<boolean>;
  tokenResetPassword: (token: string, password:string) => Promise<boolean>;
  getUserFarms:()=>void
  getUserData:(jwt?:string)=>void,
  updateUserData:(firstname:string,lastname:string,phone:string,email:string,address:string,country:string, jwt?:string)=>void,
  getFarmData:(domain:string)=>void,
  updateFarmData:(name:string, city:string,street:string,phone:string,vat:string,email:string,domain?:string)=>void,
  getFarmSettings: (domain:string) => void,
  updateFarmSettings: (domain?:string, timeZone?: {time_zone: number, time_zone_label:string}, feedDayStart?: string) => void,
  getServerIp: () => void;
  getWorkerList: (domain:string, tocloud?:boolean)=>Promise<UsersWithGroups[] | undefined>,
  addDomainWorker: (domain:string, username:string)=>Promise<void>,
  removeDomainUser: (domain:string, username:string)=>Promise<void>,
  setUserAttentionEmailSystems: (domain: string, username: string, systemIds: number[]) => Promise<void>,
  setCurrentUserAttentionEmailSystems: (domain: string, systemIds: number[]) => Promise<void>,
  getAdminInfo: () => Promise<void>,
  getUserEmailsInfo: (domain:string) => void,
  data: UserStates & PersistPartial,
  userToken?: string,
  userFarms?: UserFarm_Data[],
  userData?: User_Data
  farmData?: Farm_Data[]
  farmSettings?: CompanySettings,
  serverIp: string
  workerList?: UsersWithGroups[]
  adminInfo?: AdminInfo
  userEmails?: SystemEmailsItem[]
} 

const getUserState = (state: RootState) => {return state.graphQlData.user};
export const getFarmSettingsState = (state: RootState) => {return state.graphQlData.user.FarmSettings};
export const getWorkerListState = (state: RootState) => { return state.graphQlData.user.WorkerList };
export const getUserEmailsState = (state: RootState) => { return state.graphQlData.user.UserEmails }

export const useUser = (userToken?:string):UseUser => {
  const dispatch = useAppDispatch()
  const client = useApolloClient()

  const UserStates = useAppSelector(getUserState);
  const farmSettings = useAppSelector(getFarmSettingsState);
  const workerList = useAppSelector(getWorkerListState);
  const userEmails = useAppSelector(getUserEmailsState)
 
  const {fullState, UserToken, UserFarms, UserData, FarmData, serverIp, AdminInfo, UserEmails} = useMemo(() => {
    //TODO: UserStates.UserInfo is not array....
    var info = (UserStates.UserInfo as unknown) as any
    return {
      fullState: UserStates, 
      UserToken: UserStates.UserToken, 
      UserFarms: UserStates.UserFarms,
      UserData: info&&info[0]?info[0]:undefined,
      FarmData: UserStates.FarmInfo,
      serverIp: UserStates.serverIp,
      WorkerList: UserStates.WorkerList,
      AdminInfo: UserStates.AdminInfo,
      UserEmails: UserStates.UserEmails
    }
  }, [UserStates])
  
  useEffect(() => {
    if (userToken) {
      getUserFarms()
    }
  }, []) // ??? UserToken

  const getUserFarms = useCallback(async (company_id?:number) => {
    if (UserToken) {
      await client.query<FarmsResponse>({query:USER_FARMS, variables:{company_id} as UserFarmsArgs, fetchPolicy:"network-only"}).then(e => {
        if (e && e.data && e.data.user.companies) {
          var FarmsData = e.data.user.companies
          if (FarmsData && FarmsData.length) {
            dispatch({type:''+gqlActions.user.setUserFarms, payload:FarmsData})
          }
        }
      })
    }
  }, [UserToken, client, dispatch])
  
  const getUserEmailsInfo = async (domain: string) => {
    if (UserToken) {
      await client.query<SystemEmailsInfoResponse>({ query: SYSTEM_EMAILS_INFO, variables: { domain } as SystemEmailsItem, fetchPolicy:"no-cache", context:{ headers: { tocloud:true }}}).then(e => {
        if (e && e.data && e.data.user.systemEmails) {
          var UserEmails = e.data.user.systemEmails
          if (UserEmails) {
            dispatch({type:''+gqlActions.user.setUserEmails, payload: UserEmails})
          }
        }
      })
    }
  }

  const getUserData = async (jwt?:string) => {
    if (UserToken) {
      await client.query<UserInfoResponse>({query:USER_INFO, variables:{jwt:jwt?jwt:UserToken} as UserInfoArgs, fetchPolicy:"no-cache"}).then(e => {
        if (e && e.data && e.data.user.info) {
          //TODO: is not array.... ???
          var UserData = e.data.user.info
          if (UserData) { 
            dispatch({type:''+gqlActions.user.setUserData, payload:UserData})
          }
        }
      })
    }
  }
  const updateUserData = async (firstname:string,lastname:string,phone:string,email:string,address:string,country:string, jwt?:string) => {
    if (UserToken) {
      await client.mutate<UpdateUserInfoResponse>({mutation:UPDATE_USER_INFO, variables:{firstname, lastname, phone, email, address, country, jwt:jwt?jwt:UserToken} as UpdateUserInfoArgs, context:{ headers: { tocloud:true }}}).then(e => {
        if (e && e.data && e.data.user.updateInfo) {
          //TODO: is not array.... ???domainCalls_Reducer
          var UserData = e.data.user.updateInfo
          if (UserData && UserData.success) {
            dispatch({type:''+gqlActions.user.setUserData, payload:{firstname, lastname, phone, email, address, country} as User_Data})
          }
        }
      })
    }
  }

  const getFarmData = async (domain:string) => {
    if (UserToken) {
      await client.query<FarmInfoResponse>({query:FARM_INFO, variables:{domain} as FarmInfoArgs}).then(e => {
        if (e && e.data && e.data.company.info) {
          //TODO: is not array.... ???
          var FarmData = e.data.company.info
          if (FarmData) { 
            dispatch({type:''+gqlActions.user.setFarmData, payload:FarmData})
          }
        }
      })
    }
  }
  const updateFarmData = async (name:string, city:string,street:string,phone:string,vat:string,email:string, domain?:string) => {
   
      await client.mutate<UpdateFarmInfoResponse>({mutation:UPDATE_FARM_INFO, variables:{city, street, phone, vat, email,domain} as UpdateFarmInfoArgs, context:{ headers: { tocloud:true }}}).then(e => {
        if (e && e.data && e.data.company.updateInfo) {
          //TODO: is not array.... ???
          var FarmData = e.data.company.updateInfo
          if (FarmData && FarmData.success) {
            dispatch({type:''+gqlActions.user.setFarmData, payload:[{ name, city, street, phone, vat, email, domain }] as Farm_Data[]})
          }
        }
      }
    )
  }

  const getFarmSettings = useCallback(async (domain: string) => {
    if (UserToken) {
      await client.query<CompanySettingsResponse>({query:GET_COMPANY_SETTINGS, variables:{domain}}).then(e => {
        if (e && e.data && e.data.company.settings) {
          const timezoneOffset = DateTime.now().setZone(e.data.company.settings.time_zone_label);
          const defaultOffset = DateTime.now().setZone("Europe/Copenhagen")
          const tOffset = timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0);
          const dOffset = defaultOffset.offset - (timezoneOffset.isInDST ? 60 : 0);
          
          const timezoneInfo = timezoneOffset.isValid ? { time_zone: timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0), time_zone_label: e.data.company.settings.time_zone_label } : {time_zone: defaultOffset.offset - (defaultOffset.isInDST ? 60 : 0), time_zone_label: defaultTimezone};
          dispatch({type:''+gqlActions.user.setFarmSettings, payload: {...timezoneInfo, feed_day_start: e.data.company.settings.feed_day_start ? LocaleFarmDate.restrictTimeTo24Hours(e.data.company.settings.feed_day_start + (timezoneOffset.isValid ? tOffset : dOffset)) : timezoneOffset.isValid ? LocaleFarmDate.restrictTimeTo24Hours(tOffset) : dOffset}})
        }
      })
    }
  },[UserToken, client, dispatch])

  const updateFarmSettings = async (domain?:string, timeZone?: {time_zone: number, time_zone_label: string}, feedDayStart?: string) => {

    await client.mutate<UpdateCompanySettingsResponse>({mutation:UPDATE_COMPANY_SETTINGS, variables:{domain, timeZone, feedDayStart}}).then(e => {
      if (e && e.data && e.data.company.updateSettings) {
        const timezoneOffset = DateTime.now().setZone(e.data.company.updateSettings.time_zone_label);
        const defaultOffset = DateTime.now().setZone("Europe/Copenhagen")
        const tOffset = timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0);
        const dOffset = defaultOffset.offset - (timezoneOffset.isInDST ? 60 : 0);
        
        const timezoneInfo = timezoneOffset.isValid ? { time_zone: timezoneOffset.offset - (timezoneOffset.isInDST ? 60 : 0), time_zone_label: e.data.company.updateSettings.time_zone_label } : {time_zone: defaultOffset.offset - (defaultOffset.isInDST ? 60 : 0), time_zone_label: defaultTimezone};
        dispatch({type:''+gqlActions.user.setFarmSettings, payload: {...timezoneInfo, feed_day_start: e.data.company.updateSettings.feed_day_start ? LocaleFarmDate.restrictTimeTo24Hours(e.data.company.updateSettings.feed_day_start + (timezoneOffset.isValid ? tOffset : dOffset)) : timezoneOffset.isValid ? LocaleFarmDate.restrictTimeTo24Hours(tOffset) : dOffset}})
      }
    })
  }

  const doLogin = async (username:string, password:string):Promise<boolean> => {
    return await client.query<LoginResponse>({query:USER_LOGIN, variables:{username, password} as LoginArgs}).then(e => {
      if (e && e.data && e.data.authenticate) {
        const loginData = e.data.authenticate
        if (loginData.code + '' === "200" && loginData.message) {
          dispatch({type:''+gqlActions.user.setUserToken, payload:loginData.message})
          getUserFarms()
          return true
        }
      }
      return false
    })
  }
  const doLogOut = () => {
    if (UserToken) {
      dispatch({type:''+gqlActions.user.clear, payload:'all'}) //TODO: get actual type
    }
  }
  
  const createNewUser = async (username: string, password: string, firstname:string, lastname:string, email:string, phone:string) => {
    try {
      const createResult =  await client.mutate<CreateUserResponse>({mutation:CREATE_USER, variables:{username, password, firstname, lastname, email, phone} as CreateArgs, context:{ headers: { tocloud:true }}}).then(e => {
        if (e && e.data && e.data.user.create) {
          const createData = e.data.user.create
          if (createData && createData.success) {
            dispatch({type:''+gqlActions.user.setUserData, payload: {username, password, firstname, lastname, email, phone} as CreateArgs})
            return  true;
          }
        }
        return false;
      })
      
      if (createResult) {
        return await doLogin(username, password);
      }

      return createResult;

  } catch {
    return false;
  }
  }

  const createUserResetToken = async (username: string):Promise<boolean> => {
    return await client.mutate<CreateResetResponse>({mutation: CREATE_RESET_PASSWORD_ENTRY, variables: {username}, context:{ headers: { tocloud:true }}}).then(e => {
      if (e && e.data && e.data.user.createResetPasswordToken) {
        return e.data.user.createResetPasswordToken.success;
      }
      return false
    })
  }

  const tokenResetPassword = async (token: string, password: string) => {
    return await client.mutate<TokenResetResponse>({mutation: TOKEN_RESET_PASSWORD, variables: {token, password}, context:{ headers: { tocloud:true }}}).then(e => {
      if (e && e.data && e.data.user.resetPassword) {
        return e.data.user.resetPassword.success;
      }
      else return false
    })
  }

  const getServerIp = async () => {
    if (UserToken) {
      return await client.query<{system: {serverIp: string}}>({query: GET_SERVER_IP})
        .then((response) => {
          if (response && response.data.system.serverIp) {
            dispatch({type: String(gqlActions.user.setServerIp), payload: response.data.system.serverIp})
          }
        })
    }
  }

  const getWorkerList = useCallback(async (domain: string, tocloud: boolean = false): Promise<UsersWithGroups[] | undefined> => {
    try {
      const e = await client.query<WorkerListResponse>({query: WORKER_LIST, variables:{domain}, fetchPolicy:"network-only", context:{headers: {tocloud:tocloud}}})
      if (e && e.data && e.data.company.workerList) {          
        dispatch({type:''+gqlActions.user.setWorkerList, payload: e.data.company.workerList})
        return e.data.company.workerList;
      }
    } catch (error) {
      console.error(error)
    }
  },[client, dispatch])

  const addDomainWorker = useCallback(async (domain: string, username: string) => {
    try {
      const e = await client.mutate<AddDomainWorkerResponse>({mutation: ADD_DOMAIN_WORKER, variables:{domain, username}, context:{ headers: { tocloud:true }}})
      //TODO: consider doing a redux state update if the call would return the updated value instead
    } catch (error) {
      console.error(error)
    }
  },[client])

  const removeDomainUser = useCallback(async (domain: string, username: string) => {
    try {
      const e = await client.mutate<RemoveDomainUserResponse>({mutation: REMOVE_DOMAIN_USER, variables:{domain, username}, context:{ headers: { tocloud:true }}})
      if (e && e.data && e.data.user.removeDomainUser.success) {          
        dispatch({type:''+gqlActions.user.removeWorker, payload: username})
      }
    } catch (error) {
      console.error(error)
    }
  },[client, dispatch])

  const setUserAttentionEmailSystems = useCallback(async (domain: string, username: string, systemIds: number[]) => {
    try {
      const e = await client.mutate<SetUserAttentionEmailSystemsResponse>({mutation: SET_USER_ATTENTION_EMAIL_SYSTEMS, variables:{domain, username, systemIds}, context:{ headers: { tocloud:true }}})
    } catch (error) {
      console.error(error)
    }
  }, [client])
  
  const setCurrentUserAttentionEmailSystems = useCallback(async (domain: string, systemIds: number[]) => {
    try {
      const e = await client.mutate<SystemEmailsMutationResponse>({mutation: SYSTEM_EMAILS_MUTATION, variables: {domain, systemIds}, context:{ headers: { tocloud:true }}})
    } catch (error) {
      console.error(error)
    }
  }, [client])

  const getAdminInfo = useCallback(async () => {
    try {
      const e = await client.mutate<AdminInfoResponse>({mutation: ADMIN_INFO, context:{ headers: { tocloud:true }}})
      
      const adminInfo = e?.data?.user?.admin;
      if (adminInfo) {
        dispatch({type:''+gqlActions.user.setAdminInfo, payload: adminInfo})
      }
    } catch (error) {
      console.error(error)
    }
  },[client, dispatch])

  return {
    doLogin,
    doLogOut,
    createNewUser,
    getUserEmailsInfo,
    data: fullState,
    getUserFarms,
    userToken: UserToken,
    userFarms: UserFarms,
    getUserData,
    userData: UserData,
    updateUserData,
    getFarmData,
    farmData: FarmData,
    updateFarmData,
    farmSettings,
    getFarmSettings,
    updateFarmSettings,
    createUserResetToken,
    tokenResetPassword,
    setUserAttentionEmailSystems,
    setCurrentUserAttentionEmailSystems,
    serverIp,
    getServerIp,
    workerList,
    getWorkerList,
    addDomainWorker,
    removeDomainUser,
    getAdminInfo,
    adminInfo: AdminInfo,
    userEmails
  }
}